import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, type FieldErrors } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PasswordInput } from '@/components/ui/password-input';
import { FormError } from '@/components/ui/form-error';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Sun, Moon } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import Footer from '@/components/common/Footer';
import { cn } from '@/lib/utils';
import { newPasswordRule, FIELD_MESSAGES, REQUIRED_FIELDS_MESSAGE } from '@/lib/validation-rules';
import { focusFirstInvalidField } from '@/lib/form-validation';

const schema = z.object({
  currentPassword: z.string().min(1, 'Current password is required.'),
  newPassword: newPasswordRule,
  confirmPassword: z.string().min(1, 'Confirm your password.'),
}).superRefine((data, ctx) => {
  if (data.newPassword !== data.confirmPassword) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['confirmPassword'], message: FIELD_MESSAGES.passwordMatch });
  }
});

type FormData = z.infer<typeof schema>;

export default function ChangePasswordPage() {
  const { addToast } = useToast();
  const { theme, toggleTheme } = useTheme();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { register, handleSubmit, formState: { errors } } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onInvalid = (errs: FieldErrors<FormData>) => {
    focusFirstInvalidField(errs);
    addToast(REQUIRED_FIELDS_MESSAGE, 'error');
  };

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      await api.post('/auth/change-password', { currentPassword: data.currentPassword, newPassword: data.newPassword });
      sessionStorage.removeItem("access_token");
      queryClient.clear();
      addToast('Password changed. Please sign in again.', 'success');
      navigate('/login');
    } catch (err: any) { addToast(err.message || 'Failed to change password', 'error'); }
    setLoading(false);
  };

  return (
    <div className="login-page">
      <button onClick={toggleTheme} className="login-theme-toggle" aria-label="Toggle theme">
        {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
      </button>
      <main className="login-main">
        <div className="login-card">
          <div className="login-brand">
            <img src="/assests/SS Logo_RGB.png" alt="Departmental Labour App" className="login-logo" />
            <h1>Departmental Labour App</h1>
            <p>Change your temporary password</p>
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Set a New Password</CardTitle>
              <CardDescription>You must change your temporary password before continuing.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-4">
                <div>
                  <Label htmlFor="currentPwd">Current (temporary) password</Label>
                  <PasswordInput
                    id="currentPwd"
                    {...register('currentPassword')}
                    aria-invalid={errors.currentPassword ? true : undefined}
                    aria-describedby={errors.currentPassword ? 'currentPassword-error' : undefined}
                    className={cn(errors.currentPassword && 'border-error')}
                  />
                  <FormError id="currentPassword-error" message={errors.currentPassword?.message} />
                </div>
                <div>
                  <Label htmlFor="newPwd">New password</Label>
                  <PasswordInput
                    id="newPwd"
                    {...register('newPassword')}
                    aria-invalid={errors.newPassword ? true : undefined}
                    aria-describedby={errors.newPassword ? 'newPassword-error' : undefined}
                    className={cn(errors.newPassword && 'border-error')}
                  />
                  <FormError id="newPassword-error" message={errors.newPassword?.message} />
                </div>
                <div>
                  <Label htmlFor="confirmPwd">Confirm new password</Label>
                  <PasswordInput
                    id="confirmPwd"
                    {...register('confirmPassword')}
                    aria-invalid={errors.confirmPassword ? true : undefined}
                    aria-describedby={errors.confirmPassword ? 'confirmPassword-error' : undefined}
                    className={cn(errors.confirmPassword && 'border-error')}
                  />
                  <FormError id="confirmPassword-error" message={errors.confirmPassword?.message} />
                </div>
                <Button type="submit" className="w-full" loading={loading}>Change Password</Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
